import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { object, string } from "yup";
import {
  Box,
  Button,
  CenteredColBox,
  Form,
  Input,
  Link,
} from "../../../common/components";
import { useForm } from "../../../common/hooks/useForm";
import { spacing } from "../../../styles/theme";
import { OnboardingTitle } from "../Component/OnboardingTitle";

interface Props {
  titleKey: string;
  fieldName: string;
  labelKey: string;
  submitLabelKey: string;
  dataTestPrefix: string;
  inputType?: string;
  onNextStep: (value: string) => void;
  onPreviousStep: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  initialValue?: string;
}

export const SingleFieldStep = ({
  titleKey,
  fieldName,
  labelKey,
  submitLabelKey,
  dataTestPrefix,
  inputType,
  onNextStep,
  onPreviousStep,
  initialValue,
}: Props) => {
  const { t } = useTranslation();

  const validationSchema = useMemo(
    () =>
      object({
        [fieldName]: string().required(t("general.errors.required.input")),
      }),
    [fieldName, t]
  );

  const formik = useForm({
    initialValues: { [fieldName]: initialValue ?? "" },
    onSubmit: (values) => {
      onNextStep(values[fieldName]);
    },
    validationSchema,
  });

  return (
    <Form formik={formik}>
      <Box fullWidth mt={{ xs: spacing.xlMobile, lg: 0 }}>
        <OnboardingTitle title={t(titleKey)} />
        <CenteredColBox fullWidth>
          <Input
            type={inputType}
            id={fieldName}
            name={fieldName}
            label={t(labelKey)}
            data-test={`${dataTestPrefix}.input`}
            autoFocus
          />
        </CenteredColBox>
        <CenteredColBox
          gap={{ xs: spacing.ctaSpacingMobile, lg: spacing.ctaSpacingDesktop }}
          mt={{ xs: spacing.regularMobile, lg: spacing.regularDesktop }}
          fullWidth
        >
          <Button
            type="submit"
            fullWidth
            data-test={`${dataTestPrefix}.submitButton`}
          >
            {t(submitLabelKey)}
          </Button>
          <Link onClick={(e) => onPreviousStep(e)}>{t("onboard.back")}</Link>
        </CenteredColBox>
      </Box>
    </Form>
  );
};
